/**
 * Multi-goal / multi-event tracking per offer — operator UI (feature FS-173).
 *
 * Composition:
 *   - Header with emerald -> sky gradient headline + campaign picker.
 *   - Create-goal card: register a new named goal with its own payout, cap,
 *     and active flag.
 *   - Goals list: inline edit payout/cap, toggle active, delete.
 *   - Conversions table: recent conversions for the selected goal.
 *
 * Brand: emerald -> sky gradient heading, zinc borders, 6-8px rounded,
 * light theme, no emojis, no competitor names.
 */
import {
	useCallback,
	useEffect,
	useMemo,
	useState,
	type FormEvent,
} from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	capLabel,
	createGoal,
	deleteGoal,
	fireGoal,
	formatCents,
	formatCount,
	formatTimestampMs,
	listConversions,
	listGoals,
	multiGoalQueryKeys,
	updateGoal,
	validateCapPerClick,
	validateIdLike,
	validateName,
	validatePayoutCents,
	validateSlug,
	type CampaignGoal,
	type GoalConversion,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-rose-300 bg-white px-3 py-1.5 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60";
const HINT = "text-xs text-zinc-500";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type CreateFormState = {
	name: string;
	slug: string;
	payout_cents: string;
	cap_per_click: string;
	active: boolean;
};

const EMPTY_CREATE: CreateFormState = {
	name: "",
	slug: "",
	payout_cents: "0",
	cap_per_click: "1",
	active: true,
};

type EditState = {
	id: string;
	name: string;
	slug: string;
	payout_cents: string;
	cap_per_click: string;
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function MultiGoalPage() {
	const [campaignId, setCampaignId] = useState("");
	const [createForm, setCreateForm] = useState<CreateFormState>(EMPTY_CREATE);
	const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null);
	const [editState, setEditState] = useState<EditState | null>(null);
	const [fireClickId, setFireClickId] = useState("");

	const queryClient = useQueryClient();

	const cleanedCampaignId = validateIdLike(campaignId.trim());

	const goalsQuery = useQuery<CampaignGoal[]>({
		queryKey: multiGoalQueryKeys.list(cleanedCampaignId ?? "__none__"),
		queryFn: () => listGoals(cleanedCampaignId as string),
		enabled: cleanedCampaignId !== null,
		staleTime: 15_000,
	});

	const conversionsQuery = useQuery<GoalConversion[]>({
		queryKey: multiGoalQueryKeys.conversions(selectedGoalId ?? "__none__"),
		queryFn: () => listConversions(selectedGoalId as string, 100),
		enabled: selectedGoalId !== null,
		staleTime: 10_000,
	});

	const goals = goalsQuery.data ?? [];
	const selectedGoal = useMemo(
		() => goals.find((g) => g.id === selectedGoalId) ?? null,
		[goals, selectedGoalId],
	);

	useEffect(() => {
		// If the selected goal disappears after a delete, clear selection.
		if (selectedGoalId !== null && goals.length > 0 && !selectedGoal) {
			setSelectedGoalId(null);
		}
	}, [selectedGoalId, selectedGoal, goals.length]);

	const invalidateGoals = useCallback(() => {
		if (cleanedCampaignId !== null) {
			queryClient.invalidateQueries({
				queryKey: multiGoalQueryKeys.list(cleanedCampaignId),
			});
		}
	}, [queryClient, cleanedCampaignId]);

	const createMut = useMutation({
		mutationFn: createGoal,
		onSuccess: () => {
			toast.success("Goal created");
			setCreateForm(EMPTY_CREATE);
			invalidateGoals();
		},
		onError: (err: unknown) => {
			const message = err instanceof Error ? err.message : "create_failed";
			toast.error(message);
		},
	});

	const updateMut = useMutation({
		mutationFn: updateGoal,
		onSuccess: () => {
			toast.success("Goal updated");
			setEditState(null);
			invalidateGoals();
		},
		onError: (err: unknown) => {
			const message = err instanceof Error ? err.message : "update_failed";
			toast.error(message);
		},
	});

	const deleteMut = useMutation({
		mutationFn: deleteGoal,
		onSuccess: () => {
			toast.success("Goal deleted");
			invalidateGoals();
		},
		onError: (err: unknown) => {
			const message = err instanceof Error ? err.message : "delete_failed";
			toast.error(message);
		},
	});

	const fireMut = useMutation({
		mutationFn: fireGoal,
		onSuccess: () => {
			toast.success("Conversion fired");
			setFireClickId("");
			if (selectedGoalId) {
				queryClient.invalidateQueries({
					queryKey: multiGoalQueryKeys.conversions(selectedGoalId),
				});
			}
		},
		onError: (err: unknown) => {
			const message = err instanceof Error ? err.message : "fire_failed";
			toast.error(message);
		},
	});

	const handleCreate = (e: FormEvent): void => {
		e.preventDefault();
		if (cleanedCampaignId === null) {
			toast.error("Enter a campaign ID first");
			return;
		}
		const name = validateName(createForm.name);
		const slug = validateSlug(createForm.slug);
		const payout = validatePayoutCents(createForm.payout_cents);
		const cap = validateCapPerClick(createForm.cap_per_click);
		if (name === null) {
			toast.error("Invalid goal name");
			return;
		}
		if (slug === null) {
			toast.error("Invalid slug");
			return;
		}
		if (payout === "invalid") {
			toast.error("Invalid payout");
			return;
		}
		if (cap === "invalid") {
			toast.error("Invalid cap per click");
			return;
		}
		createMut.mutate({
			campaign_id: cleanedCampaignId,
			name,
			slug,
			payout_cents: payout,
			cap_per_click: cap,
			active: createForm.active,
		});
	};

	const handleEditSave = (e: FormEvent): void => {
		e.preventDefault();
		if (!editState) return;
		const name = validateName(editState.name);
		const slug = validateSlug(editState.slug);
		const payout = validatePayoutCents(editState.payout_cents);
		const cap = validateCapPerClick(editState.cap_per_click);
		if (name === null) {
			toast.error("Invalid goal name");
			return;
		}
		if (slug === null) {
			toast.error("Invalid slug");
			return;
		}
		if (payout === "invalid") {
			toast.error("Invalid payout");
			return;
		}
		if (cap === "invalid") {
			toast.error("Invalid cap per click");
			return;
		}
		updateMut.mutate({
			id: editState.id,
			name,
			slug,
			payout_cents: payout,
			cap_per_click: cap,
		});
	};

	const handleToggleActive = (goal: CampaignGoal) => {
		updateMut.mutate({ id: goal.id, active: !goal.active });
	};

	const handleDelete = (goal: CampaignGoal) => {
		if (!window.confirm(`Delete goal "${goal.name}"?`)) return;
		deleteMut.mutate(goal.id);
	};

	const handleFire = (e: FormEvent): void => {
		e.preventDefault();
		if (!selectedGoalId) return;
		const clickId = validateIdLike(fireClickId.trim());
		if (clickId === null) {
			toast.error("Invalid click_id");
			return;
		}
		fireMut.mutate({ id: selectedGoalId, click_id: clickId });
	};

	return (
		<main className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-6 py-10">
			<header className="flex flex-col gap-2">
				<p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
					Campaigns
				</p>
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
					Multi-goal tracking
				</h1>
				<p className="text-sm text-zinc-600">
					Register multiple named goals per campaign. Each goal has its own
					payout, per-click cap, and active state, so a single click can
					produce one conversion per goal up to the cap.
				</p>
			</header>

			<section className={`${CARD} p-5`}>
				<label className={LABEL} htmlFor="mg-campaign">
					Campaign ID
				</label>
				<input
					id="mg-campaign"
					className={`${INPUT} mt-1 max-w-md`}
					value={campaignId}
					onChange={(e) => setCampaignId(e.target.value)}
					placeholder="cmp_abc123"
					autoComplete="off"
				/>
				<p className={`${HINT} mt-1`}>
					Enter the campaign whose goals you want to manage.
				</p>
			</section>

			<section className={`${CARD} p-5`}>
				<h2 className="text-base font-semibold text-zinc-900">
					Register a new goal
				</h2>
				<form
					className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2"
					onSubmit={handleCreate}
				>
					<div>
						<label className={LABEL} htmlFor="mg-name">
							Name
						</label>
						<input
							id="mg-name"
							className={`${INPUT} mt-1`}
							value={createForm.name}
							onChange={(e) =>
								setCreateForm((s) => ({ ...s, name: e.target.value }))
							}
							placeholder="First install"
							autoComplete="off"
						/>
					</div>
					<div>
						<label className={LABEL} htmlFor="mg-slug">
							Slug
						</label>
						<input
							id="mg-slug"
							className={`${INPUT} mt-1`}
							value={createForm.slug}
							onChange={(e) =>
								setCreateForm((s) => ({ ...s, slug: e.target.value }))
							}
							placeholder="install"
							autoComplete="off"
						/>
						<p className={`${HINT} mt-1`}>
							Lowercase letters, digits, dashes or underscores. Used in
							postback URLs.
						</p>
					</div>
					<div>
						<label className={LABEL} htmlFor="mg-payout">
							Payout (cents)
						</label>
						<input
							id="mg-payout"
							className={`${INPUT} mt-1`}
							inputMode="numeric"
							value={createForm.payout_cents}
							onChange={(e) =>
								setCreateForm((s) => ({
									...s,
									payout_cents: e.target.value,
								}))
							}
							placeholder="0"
						/>
					</div>
					<div>
						<label className={LABEL} htmlFor="mg-cap">
							Cap per click
						</label>
						<input
							id="mg-cap"
							className={`${INPUT} mt-1`}
							inputMode="numeric"
							value={createForm.cap_per_click}
							onChange={(e) =>
								setCreateForm((s) => ({
									...s,
									cap_per_click: e.target.value,
								}))
							}
							placeholder="1"
						/>
					</div>
					<div className="flex items-center gap-2 md:col-span-2">
						<input
							id="mg-active"
							type="checkbox"
							className="h-4 w-4 accent-emerald-600"
							checked={createForm.active}
							onChange={(e) =>
								setCreateForm((s) => ({ ...s, active: e.target.checked }))
							}
						/>
						<label htmlFor="mg-active" className={LABEL}>
							Active (accept conversions)
						</label>
					</div>
					<div className="md:col-span-2">
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={createMut.isPending || cleanedCampaignId === null}
						>
							{createMut.isPending ? "Creating..." : "Create goal"}
						</button>
					</div>
				</form>
			</section>

			<section className={`${CARD} p-5`}>
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">Goals</h2>
					<span className={HINT}>
						{formatCount(goals.length)} goal{goals.length === 1 ? "" : "s"}
					</span>
				</div>
				{cleanedCampaignId === null ? (
					<p className="mt-3 text-sm text-zinc-600">
						Enter a campaign ID to see its goals.
					</p>
				) : goalsQuery.isLoading ? (
					<p className="mt-3 text-sm text-zinc-600">Loading goals...</p>
				) : goalsQuery.isError ? (
					<p className="mt-3 text-sm text-rose-600">Failed to load goals.</p>
				) : goals.length === 0 ? (
					<p className="mt-3 text-sm text-zinc-600">
						No goals yet. Register one above.
					</p>
				) : (
					<ul className="mt-4 divide-y divide-zinc-200 border-t border-zinc-200">
						{goals.map((goal) => {
							const isEditing = editState?.id === goal.id;
							const isSelected = selectedGoalId === goal.id;
							return (
								<li key={goal.id} className="py-3">
									{isEditing && editState ? (
										<form
											className="grid grid-cols-1 gap-3 md:grid-cols-5"
											onSubmit={handleEditSave}
										>
											<input
												className={INPUT}
												value={editState.name}
												onChange={(e) =>
													setEditState((s) =>
														s ? { ...s, name: e.target.value } : s,
													)
												}
												aria-label="Goal name"
											/>
											<input
												className={INPUT}
												value={editState.slug}
												onChange={(e) =>
													setEditState((s) =>
														s ? { ...s, slug: e.target.value } : s,
													)
												}
												aria-label="Goal slug"
											/>
											<input
												className={INPUT}
												inputMode="numeric"
												value={editState.payout_cents}
												onChange={(e) =>
													setEditState((s) =>
														s
															? { ...s, payout_cents: e.target.value }
															: s,
													)
												}
												aria-label="Payout cents"
											/>
											<input
												className={INPUT}
												inputMode="numeric"
												value={editState.cap_per_click}
												onChange={(e) =>
													setEditState((s) =>
														s
															? { ...s, cap_per_click: e.target.value }
															: s,
													)
												}
												aria-label="Cap per click"
											/>
											<div className="flex items-center gap-2">
												<button
													type="submit"
													className={BTN_PRIMARY}
													disabled={updateMut.isPending}
												>
													Save
												</button>
												<button
													type="button"
													className={BTN_NEUTRAL}
													onClick={() => setEditState(null)}
												>
													Cancel
												</button>
											</div>
										</form>
									) : (
										<div className="flex items-center justify-between gap-4">
											<div className="flex flex-col">
												<div className="flex items-center gap-2">
													<button
														type="button"
														onClick={() => setSelectedGoalId(goal.id)}
														className={
															isSelected
																? "text-sm font-semibold text-sky-700"
																: "text-sm font-semibold text-zinc-900 hover:text-sky-700"
														}
													>
														{goal.name}
													</button>
													<span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-mono text-zinc-700">
														{goal.slug}
													</span>
													<span
														className={
															goal.active
																? "rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700"
																: "rounded-full bg-zinc-100 px-2 py-0.5 text-xs text-zinc-500"
														}
													>
														{goal.active ? "Active" : "Paused"}
													</span>
												</div>
												<div className={`${HINT} mt-1 flex gap-3`}>
													<span>Payout {formatCents(goal.payout_cents)}</span>
													<span>Cap {capLabel(goal.cap_per_click)}</span>
													<span>
														Updated {formatTimestampMs(goal.updated_at)}
													</span>
												</div>
											</div>
											<div className="flex gap-2">
												<button
													type="button"
													className={BTN_NEUTRAL}
													onClick={() =>
														setEditState({
															id: goal.id,
															name: goal.name,
															slug: goal.slug,
															payout_cents: String(goal.payout_cents),
															cap_per_click: String(goal.cap_per_click),
														})
													}
												>
													Edit
												</button>
												<button
													type="button"
													className={BTN_NEUTRAL}
													onClick={() => handleToggleActive(goal)}
													disabled={updateMut.isPending}
												>
													{goal.active ? "Pause" : "Activate"}
												</button>
												<button
													type="button"
													className={BTN_DANGER}
													onClick={() => handleDelete(goal)}
													disabled={deleteMut.isPending}
												>
													Delete
												</button>
											</div>
										</div>
									)}
								</li>
							);
						})}
					</ul>
				)}
			</section>

			{selectedGoal && (
				<section className={`${CARD} p-5`}>
					<div className="flex items-center justify-between">
						<h2 className="text-base font-semibold text-zinc-900">
							Conversions for {selectedGoal.name}
						</h2>
						<span className={HINT}>
							Payout {formatCents(selectedGoal.payout_cents)} &middot;{" "}
							{capLabel(selectedGoal.cap_per_click)}
						</span>
					</div>
					<form className="mt-3 flex flex-col gap-2 md:flex-row" onSubmit={handleFire}>
						<input
							className={`${INPUT} md:max-w-sm`}
							value={fireClickId}
							onChange={(e) => setFireClickId(e.target.value)}
							placeholder="click_abc123"
							aria-label="Click ID"
						/>
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={fireMut.isPending || !selectedGoal.active}
						>
							{fireMut.isPending ? "Firing..." : "Fire test conversion"}
						</button>
					</form>
					{!selectedGoal.active && (
						<p className={`${HINT} mt-2`}>
							Goal is paused. Activate it first to fire conversions.
						</p>
					)}
					<div className="mt-4 overflow-hidden rounded-[6px] border border-zinc-200">
						<table className="min-w-full divide-y divide-zinc-200 text-left text-sm">
							<thead className="bg-zinc-50 text-xs font-medium uppercase tracking-wider text-zinc-500">
								<tr>
									<th className="px-3 py-2">When</th>
									<th className="px-3 py-2">Click ID</th>
									<th className="px-3 py-2">Payout</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100 bg-white">
								{conversionsQuery.isLoading ? (
									<tr>
										<td
											className="px-3 py-4 text-center text-zinc-500"
											colSpan={3}
										>
											Loading conversions...
										</td>
									</tr>
								) : conversionsQuery.isError ? (
									<tr>
										<td
											className="px-3 py-4 text-center text-rose-600"
											colSpan={3}
										>
											Failed to load conversions.
										</td>
									</tr>
								) : (conversionsQuery.data?.length ?? 0) === 0 ? (
									<tr>
										<td
											className="px-3 py-4 text-center text-zinc-500"
											colSpan={3}
										>
											No conversions yet.
										</td>
									</tr>
								) : (
									(conversionsQuery.data ?? []).map((c) => (
										<tr key={c.id}>
											<td className="whitespace-nowrap px-3 py-2 text-zinc-700">
												{formatTimestampMs(c.created_at)}
											</td>
											<td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-zinc-700">
												{c.click_id}
											</td>
											<td className="whitespace-nowrap px-3 py-2 text-zinc-900">
												{formatCents(c.payout_cents)}
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</section>
			)}
		</main>
	);
}

export default MultiGoalPage;
